import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { EditorState } from 'lexical';
import { ProjectMetadataTokenNode } from 'modules/Lexical/nodes/ProjectMetadataTokenNode';
import { useEditModeContext } from 'modules/Lexical/Plugins/EditModeContextPlugin';
import { useEffect, useMemo } from 'react';
import { useFirstAssetNumberId } from './hooks';
import { $updateAssetNumberId } from './utils';

type AssetNumberPluginProps = {
  modifyDocument: (state: EditorState) => void;
};

export const AssetNumberPlugin = (props: AssetNumberPluginProps): null => {
  const { modifyDocument } = props;
  const [editor] = useLexicalComposerContext();
  const editMode = useEditModeContext();
  const assetNumber = useFirstAssetNumberId();

  const nodesCache = useMemo<Set<string>>(
    () => new Set(),
    [editor], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (editMode) {
      return;
    }

    return editor.registerMutationListener(
      ProjectMetadataTokenNode,
      (mutatedNodes) => {
        for (const [key, mutation] of mutatedNodes) {
          if (mutation === 'created') {
            nodesCache.add(key);
            editor.update(() => $updateAssetNumberId(key, assetNumber));
          } else if (mutation === 'destroyed') {
            nodesCache.delete(key);
          }
        }
      },
      { skipInitialization: false },
    );
  }, [editor, nodesCache, editMode, assetNumber]);

  useEffect(() => {
    if (!nodesCache.size) {
      return;
    }
    const state = editor.read(() => {
      return editor.getEditorState();
    });
    modifyDocument(state);
  }, [assetNumber, modifyDocument, nodesCache, editor]);

  return null;
};
